import { useId } from "react";

import { Box, FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import _ from "lodash";

import { useTranslation } from "@/translations";

import { ScopedBackdrop } from "../backdrop/scoped-backdrop/ScopedBackdrop";
import { useCounselors } from "./hooks/useCounselors";

interface Props {
  selectedCounselor: string | null;
  onSelectCounselor: (selectedCounselor: string | null) => void;
  /** Label of the "no filter" entry; defaults to the generic "Alle". */
  allOptionLabel?: string;
  /** Offers the "no filter" entry ahead of the counselors. Turn off where exactly one is required. */
  includeAllOption?: boolean;
  /** Prompt shown while nothing is selected and no "all" entry is offered. */
  placeholder?: string;
  ariaLabel?: string;
  minWidth?: number;
  /** Counselor to leave out, e.g. the current user beside a list that never contains their own rows. */
  excludeCounselorId?: string | null;
  /** Restricts the list to these counselors, e.g. only the current user beside the "all" entry. */
  onlyCounselorIds?: string[];
  /** Control height. Defaults to the compact 32px used by the calendar toolbar. */
  height?: number;
  /** Visible floating label. Renders a regular form-sized field instead of the compact toolbar control. */
  label?: string;
}

export function CounselorSelect({
  selectedCounselor,
  onSelectCounselor,
  allOptionLabel,
  includeAllOption = true,
  placeholder,
  ariaLabel,
  minWidth = 100,
  excludeCounselorId = null,
  onlyCounselorIds,
  height,
  label,
}: Props) {
  const { t } = useTranslation();
  const labelId = useId();
  const { counselorOptions, userCodeOf, isLoading } = useCounselors();

  const isCompact = label === undefined;
  const allLabel = allOptionLabel ?? t("common.all");
  const visibleOptions = counselorOptions.filter(
    (o) => o.id !== excludeCounselorId && (onlyCounselorIds === undefined || onlyCounselorIds.includes(o.id))
  );
  const menuItemSx = isCompact ? { fontSize: "small" } : undefined;

  // Without the "all" entry an empty value is not a filter but a gap, so it prompts for a choice
  // instead of borrowing a label for an option the list does not offer.
  const renderSelectedValue = (value: string) => {
    if (value !== "") {
      return userCodeOf(value);
    }
    return includeAllOption ? (
      allLabel
    ) : (
      <Box component="span" color="text.secondary">
        {placeholder ?? t("common.selectCounselor")}
      </Box>
    );
  };

  return (
    <ScopedBackdrop open={isLoading} size={16}>
      <Box bgcolor={"#fff"}>
        <FormControl sx={{ minWidth }}>
          {!isCompact && (
            <InputLabel id={labelId} shrink>
              {label}
            </InputLabel>
          )}
          <Select
            labelId={isCompact ? undefined : labelId}
            label={label}
            notched={isCompact ? undefined : true}
            size={isCompact ? "small" : undefined}
            color="primary"
            variant="outlined"
            value={isLoading ? "" : (selectedCounselor ?? "")}
            onChange={(e) => onSelectCounselor(_.isEmpty(e.target.value) ? null : e.target.value)}
            disabled={isLoading}
            displayEmpty
            renderValue={renderSelectedValue}
            sx={
              isCompact
                ? {
                    ...(height === undefined ? { maxHeight: 32 } : { height }),
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "action.active",
                      borderWidth: "1px",
                    },
                    fontSize: "small",
                  }
                : undefined
            }
            inputProps={{ "aria-label": isCompact ? (ariaLabel ?? t("calendar.selectUser")) : undefined }}
            aria-busy={isLoading}
          >
            {includeAllOption && (
              <MenuItem value={""} sx={menuItemSx}>
                {allLabel}
              </MenuItem>
            )}

            {visibleOptions.map((counselor) => (
              <MenuItem sx={menuItemSx} value={counselor.id} key={counselor.id}>
                {counselor.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </ScopedBackdrop>
  );
}
